import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { TopBar } from './TopBar';
import { WorkArea } from './WorkArea';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';

// Tenant user views
export type TenantViewType = 'dashboard' | 'workflows' | 'forms' | 'logs' | 'plugins' | 'secrets' | 'credentials' | 'billing' | 'settings' | 'default';

// Platform admin views
export type AdminViewType = 'admin-dashboard' | 'admin-tenants' | 'admin-users' | 'admin-credits' | 'admin-billing-parameters' | 'admin-plans' | 'admin-plugins' | 'admin-templates' | 'admin-promo-codes' | 'admin-referral-codes' | 'admin-subscriptions' | 'admin-invoices' | 'admin-email-audit-logs' | 'admin-settings';

// Combined view type
export type ViewType = TenantViewType | AdminViewType;

export interface LayoutProps {
  children?: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Function to determine view from pathname
  const getViewFromPath = (pathname: string): ViewType => {
    // Admin routes
    if (pathname.startsWith('/admin/dashboard')) return 'admin-dashboard';
    if (pathname.startsWith('/admin/tenants')) return 'admin-tenants';
    if (pathname.startsWith('/admin/users')) return 'admin-users';
    if (pathname.startsWith('/admin/credits')) return 'admin-credits';
    if (pathname.startsWith('/admin/billing/parameters')) return 'admin-billing-parameters';
    if (pathname.startsWith('/admin/billing/plans')) return 'admin-plans';
    if (pathname.startsWith('/admin/billing/subscriptions')) return 'admin-subscriptions';
    if (pathname.startsWith('/admin/billing/invoices')) return 'admin-invoices';
    if (pathname.startsWith('/admin/plugins')) return 'admin-plugins';
    if (pathname.startsWith('/admin/templates')) return 'admin-templates';
    if (pathname.startsWith('/admin/promo-codes')) return 'admin-promo-codes';
    if (pathname.startsWith('/admin/referral-codes')) return 'admin-referral-codes';
    if (pathname.startsWith('/admin/email-audit-logs')) return 'admin-email-audit-logs';
    if (pathname.startsWith('/admin/settings')) return 'admin-settings';
    // Tenant routes
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/workflows')) return 'workflows';
    if (pathname.startsWith('/forms')) return 'forms';
    if (pathname.startsWith('/logs')) return 'logs';
    if (pathname.startsWith('/plugins')) return 'plugins';
    if (pathname.startsWith('/credentials')) return 'credentials';
    if (pathname.startsWith('/secrets')) return 'secrets';
    if (pathname.startsWith('/billing')) return 'billing';
    if (pathname.startsWith('/settings')) return 'settings';
    return 'default';
  };

  const [currentView, setCurrentView] = useState<ViewType>(() =>
    getViewFromPath(location.pathname)
  );

  // Update currentView when location changes
  useEffect(() => {
    const newView = getViewFromPath(location.pathname);
    setCurrentView(newView);
  }, [location.pathname]);

  const handleNavigate = (view: ViewType) => {
    // Navigate using React Router instead of manual history manipulation
    switch (view) {
      // Admin routes
      case 'admin-dashboard':
        navigate('/admin/dashboard');
        break;
      case 'admin-tenants':
        navigate('/admin/tenants');
        break;
      case 'admin-users':
        navigate('/admin/users');
        break;
      case 'admin-credits':
        navigate('/admin/credits');
        break;
      case 'admin-billing-parameters':
        navigate('/admin/billing/parameters');
        break;
      case 'admin-plans':
        navigate('/admin/billing/plans');
        break;
      case 'admin-subscriptions':
        navigate('/admin/billing/subscriptions');
        break;
      case 'admin-invoices':
        navigate('/admin/billing/invoices');
        break;
      case 'admin-plugins':
        navigate('/admin/plugins');
        break;
      case 'admin-templates':
        navigate('/admin/templates');
        break;
      case 'admin-promo-codes':
        navigate('/admin/promo-codes');
        break;
      case 'admin-referral-codes':
        navigate('/admin/referral-codes');
        break;
      case 'admin-email-audit-logs':
        navigate('/admin/email-audit-logs');
        break;
      case 'admin-settings':
        navigate('/admin/settings');
        break;
      // Tenant routes
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'workflows':
        navigate('/workflows');
        break;
      case 'logs':
        navigate('/logs');
        break;
      case 'plugins':
        navigate('/plugins');
        break;
      case 'credentials':
        navigate('/credentials');
        break;
      case 'secrets':
        navigate('/secrets');
        break;
      case 'billing':
        navigate('/billing');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'forms':
        navigate('/forms');
        break;
      default:
        navigate('/');
    }
    setCurrentView(view);
  };

  return (
    <SidebarProvider>
      <AppSidebar onNavigate={handleNavigate} currentView={currentView} />
      <SidebarInset>
        {/* Header with sidebar trigger */}
        <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <TopBar />
        </header>

        {/* Work Area */}
        <WorkArea currentView={currentView}>
          {children}
        </WorkArea>
      </SidebarInset>
    </SidebarProvider>
  );
};